/**
 * 船舶位置が3秒以上離れている場合に線形補間した船舶位置を返却する。
 * @param vesselPosition
 */
import { List } from "immutable";
import moment from "moment";
import Location from "./collisionWarning/Location";
import * as CollisionLogicUtil from "./collisionWarning/CollisionLogicUtil";
import * as LightWeightGlobalLogic from "./collisionWarning/LightWeightGlobalLogic";
import { VESSEL_CATEGORY } from "@/config/const";
import * as VesselUtil from "@/utils/VesselUtil";
import * as MapVesselUtil from "@/utils/MapVesselUtil";
import Colors from "@/config/Colors";
import * as turf from "@turf/turf";
import * as geolib from "geolib";
import { WakeLineUtil } from "@/utils/index";

export const getVesselMovement = (vesselPositions, time, vessel) => {
  const org = List(vesselPositions).sortBy(
    value => value,
    (valueA, valueB) => {
      const valueADateTime = moment(
        valueA.datetime.replace("UTC", ""),
        moment.ISO_8601
      ).utc();
      const valueBDateTime = moment(
        valueB.datetime.replace("UTC", ""),
        moment.ISO_8601
      ).utc();
      return valueBDateTime.isAfter(valueADateTime) ? 1 : -1;
    }
  );
  const justBeforeIndex = org.findIndex(value =>
    moment(value.datetime.replace("UTC", ""), moment.ISO_8601)
      .utc()
      .isSameOrBefore(time)
  );
  const justAfter = org.get(justBeforeIndex - 1);
  const justBefore = org.get(justBeforeIndex);
  const nextBefore = org.get(justBeforeIndex + 1);

  const location = getLocation(time, justAfter, justBefore, vessel.cat.id);
  if (
    justBefore &&
    justBefore.direction &&
    justBefore.velocity &&
    justBefore.heading
  ) {
    return {
      id: vessel.id,
      name: vessel.n_j ? vessel.n_j : vessel.n,
      timestamp: time,
      location: location,
      direction: justBefore.direction,
      heading: justBefore.heading,
      velocity: justBefore.velocity,
      category: vessel.cat.id,
      length: vessel.size ? vessel.size.l : null,
      width: vessel.size ? vessel.size.w : null,
    };
  }

  if (justAfter) {
    const { direction, velocity } = getVector(justAfter, justBefore);
    if (!justBefore.direction) {
      justBefore.direction = direction;
    }
    if (!justBefore.velocity) {
      justBefore.velocity = velocity;
    }
    if (!justBefore.heading) {
      justBefore.heading = direction;
    }

    return {
      id: vessel.id,
      name: vessel.n_j ? vessel.n_j : vessel.n,
      timestamp: time,
      location: location,
      direction: justBefore.direction,
      heading: justBefore.heading,
      velocity: justBefore.velocity,
      category: vessel.cat.id,
      length: vessel.size ? vessel.size.l : null,
      width: vessel.size ? vessel.size.w : null,
    };
  }

  if (
    nextBefore &&
    nextBefore.direction &&
    nextBefore.velocity &&
    nextBefore.heading
  ) {
    return {
      id: vessel.id,
      name: vessel.n_j ? vessel.n_j : vessel.n,
      timestamp: time,
      location: location,
      direction: nextBefore.direction,
      heading: nextBefore.heading,
      velocity: nextBefore.velocity,
      category: vessel.cat.id,
      length: vessel.size ? vessel.size.l : null,
      width: vessel.size ? vessel.size.w : null,
    };
  }

  if (nextBefore) {
    const { direction, velocity } = getVector(justBefore, nextBefore);
    if (!nextBefore.direction) {
      nextBefore.direction = direction;
    }
    if (!nextBefore.velocity) {
      nextBefore.velocity = velocity;
    }
    if (!nextBefore.heading) {
      nextBefore.heading = direction;
    }

    return {
      id: vessel.id,
      name: vessel.n_j ? vessel.n_j : vessel.n,
      timestamp: time,
      location: location,
      direction: nextBefore.direction,
      heading: nextBefore.heading,
      velocity: nextBefore.velocity,
      category: vessel.cat.id,
      length: vessel.size ? vessel.size.l : null,
      width: vessel.size ? vessel.size.w : null,
    };
  }

  const dir = justBefore.direction ? justBefore.direction : 0;
  const heading = justBefore.heading ? justBefore.heading : dir;
  return {
    id: vessel.id,
    name: vessel.n_j ? vessel.n_j : vessel.n,
    timestamp: time,
    location: location,
    direction: dir,
    heading: heading,
    velocity: 0,
    category: vessel.cat.id,
    length: vessel.size ? vessel.size.l : null,
    width: vessel.size ? vessel.size.w : null,
  };
};

const getVector = (after, before) => {
  if (
    after.latitude === before.latitude &&
    after.longitude === before.longitude
  ) {
    return {
      direction: 0,
      velocity: 0,
    };
  }

  const move = LightWeightGlobalLogic.getMovement(
    new Location(before.latitude, before.longitude),
    new Location(after.latitude, after.longitude)
  );

  let dir = 0;
  if (isFinite(move.direction)) {
    dir = move.direction;
  }

  const afterTime = moment(
    after.datetime.replace("UTC", ""),
    moment.ISO_8601
  ).utc();
  const beforeTime = moment(
    before.datetime.replace("UTC", ""),
    moment.ISO_8601
  ).utc();
  const timeDiff = Math.max(afterTime.diff(beforeTime), 0.0001);
  const vel =
    (move.distance * 60.0 * 60.0) / timeDiff / CollisionLogicUtil.MILE;
  return {
    direction: dir,
    velocity: vel,
  };
};

const getLocation = (time, justAfter, justBefore, vesselCategoryId) => {
  const beforeTime = moment(
    justBefore.datetime.replace("UTC", ""),
    moment.ISO_8601
  ).utc();
  const timeBefore = time.diff(beforeTime);

  if (justAfter) {
    const afterTime = moment(
      justAfter.datetime.replace("UTC", ""),
      moment.ISO_8601
    ).utc();
    const timeAfter = afterTime.diff(time);
    return new Location(
      (timeAfter * justBefore.latitude + timeBefore * justAfter.latitude) /
        (timeAfter + timeBefore),
      (timeAfter * justBefore.longitude + timeBefore * justAfter.longitude) /
        (timeAfter + timeBefore)
    );
  }

  const getExpirationTime = () => {
    switch (vesselCategoryId) {
      default:
        return 7.0 * 60.0;
      case 6: // Company
      case 7: // App
      case 8: // JetSki
        return 60.0;
    }
  };
  if (time.isSameOrAfter(beforeTime.add(getExpirationTime(), "seconds"))) {
    return new Location(justBefore.latitude, justBefore.longitude);
  }

  return new Location(justBefore.latitude, justBefore.longitude);
};

/**
 * Point feature 生成 (船舶表示)
 * @param vesselId
 * @param data
 * @param vesselInfo
 * @param currentEpochSeconds
 * @returns {{geometry: {coordinates: *[], type: string}, type: string, properties: {tco: string, c: *, d: *, h: *, i: string, co: *, n: string, o: number, r: *, s: *, t: *, v: *, id: *}}}
 */
export const createPointFeature = (
  vesselId,
  data,
  vesselInfo,
  currentEpochSeconds
) => {
  let time = moment(data.t).unix();
  let diff = currentEpochSeconds - time;
  let category = parseInt(
    vesselInfo && vesselInfo.cat ? vesselInfo.cat.id : VESSEL_CATEGORY.OTHER
  );
  let size = vesselInfo.size;

  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [data.lon, data.lat],
    },
    properties: {
      id: vesselId,
      d: data.d,
      h: data.h,
      s: data.s,
      t: data.t,
      v: data.v,
      r: data.h || data.d,
      c: category,
      i: VesselUtil.getSymbolIconNameLightWeight(category, data.v, diff),
      o: VesselUtil.getSimpleOpacity(category, diff),
      tco: "#ffffff",
      co: VESSEL_CATEGORY.properties[category].color,
      n: vesselInfo ? (vesselInfo.n_j ? vesselInfo.n_j : vesselInfo.n) : "",
      is_size: size && size.l && size.w ? 1 : 0,
    },
  };
};

/**
 * Line string feature 生成 (航跡)
 *
 * @param vesselId
 * @param vesselInfo
 * @param wakeHistories
 * @returns {Array}
 */
export const createWakeLineFeatures = coordinates => {
  return {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: coordinates,
    },
    properties: {},
  };
};

/**
 * Vector feature 生成 (方位速度ベクトル表示)
 * @param vesselId
 * @param position
 * @param info
 * @returns {Array}
 */
export const createPolygonFeature = (vesselId, position, info) => {
  let vesselFeatures = [];

  if (info) {
    if (!position.l) {
      position.l = {
        latitude: position.lat,
        longitude: position.lon,
        _lat: position.lat,
        _long: position.lon,
      };
    }

    const vesselBody = MapVesselUtil.createVesselBodyCoords(info, position);
    if (vesselBody) {
      const aisTriangle = MapVesselUtil.createAISTriangleCoords(info, position);
      const starboardLight = MapVesselUtil.createStarboardLightCoords(
        vesselBody
      );
      const portLight = MapVesselUtil.createPortLightCoords(vesselBody);
      const color = Colors.getVesselCategoryColor(info.c);

      vesselFeatures.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [vesselBody.backRight.longitude, vesselBody.backRight.latitude],
              [vesselBody.frontRight.longitude, vesselBody.frontRight.latitude],
              [
                vesselBody.frontCenter.longitude,
                vesselBody.frontCenter.latitude,
              ],
              [vesselBody.frontLeft.longitude, vesselBody.frontLeft.latitude],
              [vesselBody.backLeft.longitude, vesselBody.backLeft.latitude],
              [vesselBody.backRight.longitude, vesselBody.backRight.latitude],
            ],
          ],
        },
        properties: {
          id: vesselId,
          c: info.c || 9,
          co: color,
        },
      });

      vesselFeatures.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [portLight || []],
        },
        properties: {
          id: vesselId,
          c: info.c || 9,
          co: "#FF0002",
        },
      });
      vesselFeatures.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [starboardLight || []],
        },
        properties: {
          id: vesselId,
          c: info.c || 9,
          co: "#54FF00",
        },
      });
      vesselFeatures.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [aisTriangle || []],
        },
        properties: {
          id: vesselId,
          c: info.c || 9,
          co: "#FFFFFF",
        },
      });
    }
  }

  return vesselFeatures;
};

export const createPointsArray = points => {
  const pointsArray = [];
  points.forEach((currentPoint, index) => {
    const nextPoint = index + 1 < points.length ? points[index + 1] : null;
    let heading = 0;
    if (nextPoint) {
      if (!currentPoint.h || currentPoint.h === 511) {
        let pointFrom = turf.point([currentPoint.lon, currentPoint.lat]);
        let pointTo = turf.point([nextPoint.lon, nextPoint.lat]);
        heading = turf.bearing(pointFrom, pointTo);
      } else {
        heading = currentPoint.h;
      }
      const prevPoint = index - 1 < points.length ? points[index - 1] : null;
      let timeDiff = 0;
      if (prevPoint) {
        let dateTo = moment.unix(currentPoint.t);
        let dateFrom = moment.unix(prevPoint.t);
        timeDiff = dateFrom.diff(dateTo, "minutes");
      }

      pointsArray.push({
        lat: currentPoint.lat,
        lon: currentPoint.lon,
        speed: currentPoint.v || 0,
        heading,
        epoch: currentPoint.t,
        timeDiff,
      });
    } else {
      if (!currentPoint.h || currentPoint.h === 511) {
        const prevPoint = index - 1 < points.length ? points[index - 1] : null;
        if (prevPoint) {
          let pointFrom = turf.point([prevPoint.lon, prevPoint.lat]);
          let pointTo = turf.point([currentPoint.lon, currentPoint.lat]);
          heading = turf.bearing(pointFrom, pointTo);
        }
      } else {
        heading = currentPoint.h;
      }
      pointsArray.push({
        lat: currentPoint.lat,
        lon: currentPoint.lon,
        speed: currentPoint.v,
        heading,
        epoch: currentPoint.t,
        timeDiff: 0,
      });
    }
  });

  return pointsArray;
};

export const createPointsArrayForHistory = points => {
  const pointsArray = [];
  points.forEach((currentPoint, index) => {
    const nextPoint = index + 1 < points.length ? points[index + 1] : null;
    let heading = 0;
    if (nextPoint) {
      if (!currentPoint.h || currentPoint.h === 511) {
        let pointFrom = turf.point([currentPoint.lon, currentPoint.lat]);
        let pointTo = turf.point([nextPoint.lon, nextPoint.lat]);
        heading = turf.bearing(pointFrom, pointTo);
      } else {
        heading = currentPoint.h;
      }
      const prevPoint = index - 1 < points.length ? points[index - 1] : null;
      let timeDiff = 0;
      if (prevPoint) {
        let dateTo = moment.unix(currentPoint.t);
        let dateFrom = moment.unix(prevPoint.t);
        timeDiff = dateFrom.diff(dateTo, "minutes");
      }

      pointsArray.push({
        lat: currentPoint.lat,
        lon: currentPoint.lon,
        speed: currentPoint.v || 0,
        heading, //
        d: currentPoint.d, // d使用箇所
        epoch: currentPoint.t,
        timeDiff,
      });
    } else {
      if (!currentPoint.h || currentPoint.h === 511) {
        const prevPoint = index - 1 < points.length ? points[index - 1] : null;
        if (prevPoint) {
          let pointFrom = turf.point([prevPoint.lon, prevPoint.lat]);
          let pointTo = turf.point([currentPoint.lon, currentPoint.lat]);
          heading = turf.bearing(pointFrom, pointTo);
        }
      } else {
        heading = currentPoint.h;
      }
      pointsArray.push({
        lat: currentPoint.lat,
        lon: currentPoint.lon,
        speed: currentPoint.v,
        heading,
        d: currentPoint.d, // d使用箇所
        epoch: currentPoint.t,
        timeDiff: 0,
      });
    }
  });

  return pointsArray;
};

export const vessel = date => {
  const vessel = {
    vesselId: date.id,
    vesselName: date.n_j || date.n || "UNKNOWN",
    mmsi: date.mmsi || "",
    vesselType: date.cat,
    size: date.size,
    pointsArray: createPointsArray(date.points),
    polygonData: Object.assign({ c: date.cat }, date.size),
    hasSize: date.size.w !== null,
  };

  return vessel;
};

export const vesselForRgHistory = date => {
  const vessel = {
    vesselId: date.id,
    vesselName: date.n_j || date.n || "UNKNOWN",
    mmsi: date.mmsi || "",
    vesselType: date.cat, // catをvesselTypeにしている
    size: date.size,
    pointsArray: createPointsArrayForHistory(date.points),
    polygonData: Object.assign({ c: date.cat }, date.size),
    hasSize: date.size.w !== null,
  };

  return vessel;
};


export const createChunkPoints = (
  pointsArray,
  distance = 1000,
  ignoreVesselId
) => {
  let chunkPoints = [];
  let pointsStart = pointsArray[0];
  let pointsEnd = pointsArray[pointsArray.length - 1];

  let dateTo = moment.unix(pointsEnd.epoch);
  let dateFrom = moment.unix(pointsStart.epoch);

  let diff = dateTo.diff(dateFrom, "minutes");
  if (diff >= 30) {
    let nextPointTime = moment.unix(pointsStart.epoch).add(30, "minutes");
    const middlePoints = pointsArray.find(
      data => data.epoch >= nextPointTime.unix()
    );
    chunkPoints.push(
      chunkPoint(
        pointsStart.lon,
        pointsStart.lat,
        pointsStart.epoch,
        middlePoints.epoch,
        distance,
        ignoreVesselId
      )
    );
    chunkPoints.push(
      chunkPoint(
        middlePoints.lon,
        middlePoints.lat,
        middlePoints.epoch - 600,
        middlePoints.epoch + 600,
        distance,
        ignoreVesselId
      )
    );
    chunkPoints.push(
      chunkPoint(
        pointsEnd.lon,
        pointsEnd.lat,
        pointsEnd.epoch - 600,
        pointsEnd.epoch,
        distance,
        ignoreVesselId
      )
    );
  } else {
    chunkPoints.push(
      chunkPoint(
        pointsStart.lon,
        pointsStart.lat,
        pointsStart.epoch,
        pointsEnd.epoch - 1,
        distance,
        ignoreVesselId
      )
    );
    chunkPoints.push(
      chunkPoint(
        pointsEnd.lon,
        pointsEnd.lat,
        pointsEnd.epoch,
        pointsEnd.epoch,
        distance,
        ignoreVesselId
      )
    );
  }

  return chunkPoints;
};

export const chunkPoint = (
  lon,
  lat,
  startedAtUnix,
  endedAtUnix,
  distance = 1000,
  ignoreVesselId
) => {
  let bounds = geolib.getBoundsOfDistance(
    { latitude: lat, longitude: lon },
    distance
  );

  let chunkPoint = {
    started_at:
      moment
        .unix(startedAtUnix)
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss.000") + "Z",
    ended_at:
      moment
        .unix(endedAtUnix)
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss.000") + "Z",
    north_east_latitude: bounds[1].latitude,
    north_east_longitude: bounds[1].longitude,
    south_west_latitude: bounds[0].latitude,
    south_west_longitude: bounds[0].longitude,
    ignore_vessel_ids: ignoreVesselId,
  };

  return chunkPoint;
};

export const createTrackFeatureCollection = points => {
  if (points.length <= 1) return;

  let features = [];
  let prevCoordinates;
  let prevProps;
  for (const point of points) {
    let props = {
      color: WakeLineUtil.getVelocityColor(point.speed),
      opacity: 0.9,
      epoch: point.epoch,
    };

    let currentCoordinates = [point.lon, point.lat];
    if (!prevCoordinates) {
      prevCoordinates = currentCoordinates;
      prevProps = props;
      continue;
    }

    let linestring = turf.lineString(
      [prevCoordinates, currentCoordinates],
      prevProps
    );
    features.push(linestring);
    prevCoordinates = currentCoordinates;
    prevProps = props;
  }

  return turf.featureCollection(features);
};

export const createTrackFeatures = (
  objectType,
  vesselId,
  visible,
  points,
  idx,
  lastCoordinates,
  rgObject
) => {
  if (points.length <= 1) return [];

  const features = [];
  let prevCoordinates;
  let prevProps;
  for (let i = 0; i < idx; i++) {
    const props = {
      objectType: objectType,
      vesselId: vesselId,
      visible: visible,
      color: WakeLineUtil.getVelocityColor(points[i].speed),
      opacity: 0.9,
      epoch: points[i].epoch || points[i].t,
      speed: points[i].speed,
      lat: points[i].lat,
      lon: points[i].lon,
      name: rgObject.vesselName, // 履歴のポイントと同じ名前
    };
    const currentCoordinates = [points[i].lon, points[i].lat];
    if (!prevCoordinates) {
      prevCoordinates = currentCoordinates;
      prevProps = props;
      continue;
    }

    features.push(
      turf.lineString([prevCoordinates, currentCoordinates], prevProps)
    );
    prevCoordinates = currentCoordinates;
    prevProps = props;
  }

  features.push(turf.lineString([prevCoordinates, lastCoordinates], prevProps)); // 最後の点と最新の点を結ぶ線を追加

  return features;
};
